import { notify } from '@eleveo/components-react';
import { AxiosInstance } from 'axios';
import { useQuery, UseQueryOptions } from 'react-query';

import { ELEVEO_USER_ROLES_ROOT_KEY } from './eleveoRolesQuery';
import { ErrorHandlerFunction } from './types';
import { useDataProvider } from '../app/DataProvider';
import { ETL_INTEGRATION_TYPE, Values } from '../components/configuration/types';
import { RoleOption } from '../components/forms/roles/types';
import { EndpointsFactory, IntegrationRequestBodyType } from '../constants/integrationConstants';

const USER_ROLES_POSTFIX = '-user-roles';

export const handleIntegrationRolesErrors: ErrorHandlerFunction = (queryKey, t) => {
  if (Array.isArray(queryKey)) {
    const isIntegrationRolesError =
      queryKey[0].includes(USER_ROLES_POSTFIX) && queryKey[0] !== ELEVEO_USER_ROLES_ROOT_KEY;

    if (isIntegrationRolesError) {
      notify.error(
        t('myConfigurations.roleMapping.response.failedIntegrationRoles', {
          providerName: t(`providers.${String(queryKey[0]).replace(USER_ROLES_POSTFIX, '')}`),
        })
      );
    }

    return isIntegrationRolesError;
  }

  return false;
};

export const createIntegrationRolesKey = (integration: Values<typeof ETL_INTEGRATION_TYPE>) =>
  `${integration}${USER_ROLES_POSTFIX}` as const;

const getCorrectFetchFunction = (
  client: AxiosInstance,
  integration: Values<typeof ETL_INTEGRATION_TYPE>,
  data: IntegrationRequestBodyType
): (() => Promise<RoleOption[]>) => {
  switch (integration) {
    case ETL_INTEGRATION_TYPE.wxcc:
      return () => EndpointsFactory.getWebexCCUserRoles(client, data);
    case ETL_INTEGRATION_TYPE.zoomcc:
      return () => EndpointsFactory.getZoomCCUserRoles(client, data);
    case ETL_INTEGRATION_TYPE.msteams:
      return () => EndpointsFactory.getMSTeamsUserRoles(client, data);
    case ETL_INTEGRATION_TYPE.wxcalling:
      return () => EndpointsFactory.getWebexCCUserRoles(client, data); // TODO change to webex calling roles endpoint
    default:
      throw new Error('Received invalid integration!');
  }
};

export function useIntegrationRolesQuery(
  integrationToRequest: Values<typeof ETL_INTEGRATION_TYPE>,
  { clientId, clientSecret, organizationId, apiUrl }: IntegrationRequestBodyType = {
    clientId: '',
    clientSecret: '',
    organizationId: '',
    apiUrl: '',
  },
  options: UseQueryOptions<
    RoleOption[],
    Error,
    RoleOption[],
    [
      ReturnType<typeof createIntegrationRolesKey>,
      IntegrationRequestBodyType['clientId'],
      IntegrationRequestBodyType['clientSecret'],
      IntegrationRequestBodyType['organizationId'],
      IntegrationRequestBodyType['apiUrl']
    ]
  > = {}
) {
  const { client } = useDataProvider();
  const isEnabled = !!(clientId && clientSecret && organizationId && apiUrl);

  return useQuery(
    [createIntegrationRolesKey(integrationToRequest), clientId, clientSecret, organizationId, apiUrl],
    getCorrectFetchFunction(client, integrationToRequest, { clientId, clientSecret, organizationId, apiUrl }),
    {
      ...options,
      // query consumer should handle permissions
      enabled: isEnabled && options.enabled,
    }
  );
}
